import { useCallback, useEffect, useState } from 'react'
import {
    GripVertical,
    LayoutGrid,
    Pencil,
    Settings2,
    Sparkles,
    Trash2,
} from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Modal } from '~/components/ui/modal'
import { cn } from '~/lib/utils'
import {
    boardAI,
    getBoard,
    listBoardVersions,
    listIndicators,
    rollbackBoard,
    saveBoard,
} from '~/api/monitoring'
import type {
    BoardCard,
    BoardCardType,
    BoardVersionItem,
    MonitoringIndicator,
} from '~/api/types'
import { GridCanvas } from '~/features/monitoring/grid-canvas'
import { BoardCardBody } from '~/features/monitoring/board-cards'
import { BoardAiPanel } from '~/features/monitoring/board-ai-panel'
import { CardConfigForm } from '~/features/monitoring/board-card-config'
import {
    BoardToolbar,
    CARD_TYPES,
    TYPE_LABEL,
    cardMinSize,
} from '~/features/monitoring/board-toolbar'

const BOARD_COLS = 12

/**
 * 저장된 보드 카드를 안전 크기로 정규화한다.
 * 1) 카드 타입별 최소 w/h(그리고 열 범위)로 clamp — 예전/AI 생성 보드의 너무 작은 카드가
 *    제목·본문이 겹치던 문제를 자가 치유한다.
 * 2) 크기가 커지며 생긴 겹침은 y를 아래로만 밀어 해소한다(데이터 손실·재배치 왜곡 없음).
 * 원래 카드 순서는 유지한다. 저장은 하지 않으므로(dirty 유지 안 함) 다음 저장 때 반영된다.
 */
function normalizeBoardCards(cards: BoardCard[]): BoardCard[] {
    const sized = cards.map((c) => {
        const { minW, minH } = cardMinSize(c.type)
        const w = Math.min(BOARD_COLS, Math.max(minW, c.w))
        const h = Math.max(minH, c.h)
        const x = Math.max(0, Math.min(c.x, BOARD_COLS - w))
        return { ...c, w, h, x }
    })
    const overlaps = (a: BoardCard, b: BoardCard) =>
        a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
    const placed: BoardCard[] = []
    for (const c of [...sized].sort((a, b) => a.y - b.y || a.x - b.x)) {
        const item = { ...c }
        while (placed.some((p) => overlaps(item, p))) item.y += 1
        placed.push(item)
    }
    const byId = new Map(placed.map((p) => [p.i, p]))
    return cards.map((c) => byId.get(c.i) ?? c)
}

/** 분석보드(DIY) — 드래그·리사이즈 카드 캔버스 + 버전 저장/롤백. */
export function AnalysisBoardSection({ dashboardId }: { dashboardId: string }) {
    const [cards, setCards] = useState<BoardCard[]>([])
    const [version, setVersion] = useState(0)
    const [indicators, setIndicators] = useState<MonitoringIndicator[]>([])
    const [edit, setEdit] = useState(false)
    const [fullscreen, setFullscreen] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const [addOpen, setAddOpen] = useState(false)
    const [verOpen, setVerOpen] = useState(false)
    const [versions, setVersions] = useState<BoardVersionItem[]>([])
    // AI 빌더 패널
    const [aiOpen, setAiOpen] = useState(false)
    const [aiMsgs, setAiMsgs] = useState<
        { role: 'user' | 'ai'; content: string }[]
    >([])
    const [aiInput, setAiInput] = useState('')
    const [aiBusy, setAiBusy] = useState(false)
    // 카드 설정 모달(인라인 대신 오버레이 → 카드 크기/콘텐츠에 영향 없음)
    const [configCardId, setConfigCardId] = useState<string | null>(null)

    const load = useCallback(() => {
        getBoard(dashboardId)
            .then((b) => {
                setCards(normalizeBoardCards(b.config?.cards ?? []))
                setVersion(b.version)
                setDirty(false)
            })
            .catch(() => setCards([]))
    }, [dashboardId])

    useEffect(() => {
        load()
        listIndicators(dashboardId)
            .then(setIndicators)
            .catch(() => setIndicators([]))
    }, [dashboardId, load])

    const addCard = (type: BoardCardType) => {
        const meta = CARD_TYPES.find((t) => t.type === type)!
        const maxY = cards.reduce((m, c) => Math.max(m, c.y + c.h), 0)
        const ind = indicators[0]
        const base = {
            i: `c${Date.now()}_${cards.length}`,
            type,
            x: 0,
            y: maxY,
            w: meta.w,
            h: meta.h,
        }
        const card: BoardCard =
            type === 'text'
                ? { ...base, title: '텍스트', text: '' }
                : type === 'stat'
                  ? {
                        ...base,
                        fieldId: ind?.fieldId,
                        title: ind?.nameKo ?? meta.label,
                        statMetric: 'avg',
                        windowKind: 'all',
                        windowN: 7,
                    }
                  : {
                        ...base,
                        fieldId: ind?.fieldId,
                        title: ind?.nameKo ?? meta.label,
                    }
        setCards((prev) => [...prev, card])
        setDirty(true)
        setAddOpen(false)
    }

    const updateCard = (i: string, patch: Partial<BoardCard>) => {
        setCards((prev) =>
            prev.map((c) => (c.i === i ? { ...c, ...patch } : c))
        )
        setDirty(true)
    }
    const removeCard = (i: string) => {
        setCards((prev) => prev.filter((c) => c.i !== i))
        setDirty(true)
    }

    const onLayout = useCallback((next: BoardCard[]) => {
        setCards(next)
        setDirty(true)
    }, [])

    const save = async () => {
        setSaving(true)
        try {
            const b = await saveBoard(dashboardId, {
                config: { cards },
                note: `카드 ${cards.length}개`,
            })
            setVersion(b.version)
            setDirty(false)
        } finally {
            setSaving(false)
        }
    }

    const openVersions = () => {
        setVerOpen((v) => !v)
        if (!verOpen) {
            listBoardVersions(dashboardId)
                .then(setVersions)
                .catch(() => setVersions([]))
        }
    }
    const doRollback = async (v: number) => {
        const b = await rollbackBoard(dashboardId, v)
        setCards(normalizeBoardCards(b.config?.cards ?? []))
        setVersion(b.version)
        setDirty(false)
        setVerOpen(false)
    }

    const aiSend = async () => {
        const msg = aiInput.trim()
        if (!msg || aiBusy) return
        setAiMsgs((p) => [...p, { role: 'user', content: msg }])
        setAiInput('')
        setAiBusy(true)
        try {
            const res = await boardAI(dashboardId, {
                message: msg,
                config: { cards },
            })
            setAiMsgs((p) => [...p, { role: 'ai', content: res.message }])
            if (res.board) {
                // AI가 보드를 만들었으면 새 버전이 이미 저장됨 → 반영.
                setCards(normalizeBoardCards(res.board.config?.cards ?? []))
                setVersion(res.board.version)
                setDirty(false)
            }
        } catch {
            setAiMsgs((p) => [
                ...p,
                { role: 'ai', content: '요청을 처리하지 못했어요.' },
            ])
        } finally {
            setAiBusy(false)
        }
    }

    return (
        <section
            className={cn(
                'grid content-start gap-3',
                fullscreen &&
                    'bg-background fixed inset-0 z-50 overflow-auto p-4'
            )}>
            {/* 툴바 — 전체화면에선 아이콘 위주로 컴팩트하게(밴드 최소화) */}
            <BoardToolbar
                version={version}
                dirty={dirty}
                fullscreen={fullscreen}
                edit={edit}
                aiOpen={aiOpen}
                onToggleAi={() => setAiOpen((o) => !o)}
                onToggleEdit={() => setEdit((e) => !e)}
                onToggleFullscreen={() => setFullscreen((f) => !f)}
                addOpen={addOpen}
                onToggleAdd={() => setAddOpen((o) => !o)}
                onCloseAdd={() => setAddOpen(false)}
                onAddCard={addCard}
                verOpen={verOpen}
                versions={versions}
                onToggleVersions={openVersions}
                onCloseVersions={() => setVerOpen(false)}
                onRollback={(v) => void doRollback(v)}
                saving={saving}
                onSave={save}
            />

            <div className='flex gap-3'>
                <div className='min-w-0 flex-1'>
                    {cards.length === 0 ? (
                        <div className='border-border rounded-md border border-dashed px-6 py-14 text-center'>
                            <div className='bg-accent text-primary mx-auto grid size-12 place-items-center rounded-full'>
                                <LayoutGrid className='size-6' />
                            </div>
                            <p className='text-foreground mt-4 text-sm font-semibold'>
                                아직 카드가 없어요
                            </p>
                            <p className='text-muted-foreground mx-auto mt-1 max-w-md text-sm leading-relaxed'>
                                편집 모드에서 시계열·통계·분포·표 카드를
                                올리거나, AI 빌더에게 자연어로 맡겨 나만의
                                보드를 만들어 보세요.
                            </p>
                            <div className='mt-5 flex flex-wrap items-center justify-center gap-2'>
                                <Button
                                    onClick={() => {
                                        setEdit(true)
                                        setAddOpen(true)
                                    }}>
                                    <Pencil className='size-4' />
                                    편집 시작
                                </Button>
                                <Button
                                    variant='outline'
                                    onClick={() => setAiOpen(true)}>
                                    <Sparkles className='size-4' />
                                    AI 빌더
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <GridCanvas
                            items={cards}
                            onChange={onLayout}
                            editable={edit}
                            minSizeFor={(c) => cardMinSize(c.type)}
                            renderItem={(card, drag) => (
                                <div className='border-border bg-card flex h-full flex-col overflow-hidden rounded-md border shadow-sm'>
                                    <div
                                        className={cn(
                                            'border-border flex items-center gap-1.5 border-b px-2.5 py-1.5',
                                            edit && 'cursor-grab select-none'
                                        )}
                                        {...(edit ? drag : {})}>
                                        {edit ? (
                                            <GripVertical className='text-muted-foreground size-3.5 shrink-0' />
                                        ) : null}
                                        <span className='text-foreground min-w-0 flex-1 truncate text-xs font-semibold'>
                                            {card.title ||
                                                TYPE_LABEL[card.type] ||
                                                '카드'}
                                        </span>
                                        <Badge tone='neutral'>
                                            {TYPE_LABEL[card.type]}
                                        </Badge>
                                        {edit ? (
                                            <>
                                                <button
                                                    onPointerDown={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    onClick={() =>
                                                        setConfigCardId(card.i)
                                                    }
                                                    title='카드 설정'
                                                    className='text-muted-foreground hover:text-primary shrink-0'>
                                                    <Settings2 className='size-3.5' />
                                                </button>
                                                <button
                                                    onPointerDown={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    onClick={() =>
                                                        removeCard(card.i)
                                                    }
                                                    title='삭제'
                                                    className='text-muted-foreground hover:text-point shrink-0'>
                                                    <Trash2 className='size-3.5' />
                                                </button>
                                            </>
                                        ) : null}
                                    </div>
                                    {configCardId === card.i ? (
                                        <Modal
                                            open
                                            onClose={() =>
                                                setConfigCardId(null)
                                            }
                                            title={`카드 설정 · ${TYPE_LABEL[card.type]}`}>
                                            <CardConfigForm
                                                card={card}
                                                indicators={indicators}
                                                onChange={(patch) =>
                                                    updateCard(card.i, patch)
                                                }
                                            />
                                        </Modal>
                                    ) : null}
                                    <div className='min-h-0 flex-1 overflow-hidden p-2'>
                                        <BoardCardBody
                                            dashboardId={dashboardId}
                                            card={card}
                                        />
                                    </div>
                                </div>
                            )}
                        />
                    )}
                </div>

                {/* AI 빌더 패널 — 우측 도킹 */}
                {aiOpen ? (
                    <BoardAiPanel
                        messages={aiMsgs}
                        input={aiInput}
                        onInput={setAiInput}
                        busy={aiBusy}
                        onSend={() => void aiSend()}
                    />
                ) : null}
            </div>
        </section>
    )
}
